// Per-phone play settings (spec FE-25, FE-26): which side the stick is on, how big it is, how big
// the text is and whether the road's effects are calm (FE-10). A convenience only: kept in localStorage when the frame allows it, otherwise for as
// long as the page is open.
import { useSyncExternalStore } from 'react';
import { applyAppIcon } from './appIcon';

export type Side = 'right' | 'left';
export type StickSize = 's' | 'm' | 'l';
/** Text size on the React screens (FE-26): 90%, 100% or 120%. The world itself never scales. */
export type TextSize = 's' | 'm' | 'l';
/** The road's effects: full, or calm (no bursts, no shake, no smoke; the phone's reduced motion does the same). */
export type Effects = 'full' | 'calm';
/** The park behind a round (the crowd, distant horns, a shop's music, hawkers): on or off. */
export type ParkSound = 'on' | 'off';
export interface Prefs {
  /** Where the stick sits: bottom-left (the default, design system Orientation), the discs then
   * bottom-right; or right, mirrored. */
  side: Side;
  stick: StickSize;
  text: TextSize;
  effects: Effects;
  parkSound: ParkSound;
}

/** The stick's travel in CSS pixels for each size (the base is twice this across). */
export const STICK_RADIUS: Record<StickSize, number> = { s: 48, m: 60, l: 76 };

const KEY = 'wahala.prefs';
/** Saves before v2 wrote side 'right' whenever any setting changed, chosen or not: those fall back
 * to the left. */
const VERSION = 2;
const DEFAULTS: Prefs = { side: 'left', stick: 'm', text: 'm', effects: 'full', parkSound: 'on' };
let current: Prefs = load();
applyText(current.text);
// the installed app wears the season's icon (FE-09)
applyAppIcon('season');
const listeners = new Set<() => void>();

function load(): Prefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Prefs> & { v?: number };
    return {
      side: raw.v === VERSION && raw.side === 'right' ? 'right' : 'left',
      stick: raw.stick === 's' || raw.stick === 'l' ? raw.stick : 'm',
      text: raw.text === 's' || raw.text === 'l' ? raw.text : 'm',
      effects: raw.effects === 'calm' ? 'calm' : 'full',
      parkSound: raw.parkSound === 'off' ? 'off' : 'on',
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/** The text size lives on <html> so every screen, sheet and overlay picks it up from CSS. */
function applyText(t: TextSize): void {
  if (typeof document !== 'undefined') document.documentElement.dataset.text = t;
}

export function setPrefs(patch: Partial<Prefs>): void {
  current = { ...current, ...patch };
  applyText(current.text);
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...current, v: VERSION }));
  } catch {
    // storage blocked in the frame: the choice lasts until the page closes
  }
  for (const l of listeners) l();
}

/** The settings now, for code outside React (the world reads this each frame: no copy is made). */
export function getPrefs(): Readonly<Prefs> {
  return current;
}

/** Hear about changes outside React (the park's sound). */
export function onPrefs(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}
